import { useEffect, useRef } from "react";
import { GALAXY_STARS } from "@/lib/galaxy_data";
import { useReducedMotion } from "@/lib/motion";

/** Seeded so the sky is the same on every visit. */
export function rng(seed: number) {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Dot = { x: number; y: number; r: number; color: string; dur: number; delay: number; depth: number };

const COLORS = ["#FFFFFF", "#BFD8FF", "#C9A8FF", "#9FE8FF", "#FFE58A"];

/** Jake's 46 design stars (twice over on wide screens) plus a dust of faint ones, in viewport fractions. */
function makeStars(wide: boolean): Dot[] {
  const rnd = rng(11);
  const out: Dot[] = [];
  const copies = wide ? 2 : 1;
  for (let c = 0; c < copies; c++)
    for (const [x, y, d, color, dur, delay] of GALAXY_STARS)
      out.push({ x: (x / 390 + c * 0.37) % 1, y: (y / 844 + c * 0.53) % 1, r: d / 2, color, dur, delay: delay + c, depth: 0.5 + rnd() * 0.5 });
  for (let i = 0; i < (wide ? 160 : 70); i++)
    out.push({ x: rnd(), y: rnd(), r: 0.4 + rnd() * 0.8, color: COLORS[Math.floor(rnd() * COLORS.length)]!, dur: 2 + rnd() * 3, delay: rnd() * 3, depth: rnd() * 0.5 });
  return out;
}

/**
 * The page's sky: a fixed, full-viewport star field behind everything. Stars twinkle like the app's
 * (alpha .25 + .75k, radius x (.8 + .35k)) and drift a little with scroll for depth. One canvas, ~30 fps.
 */
export function Sky() {
  const ref = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const cv = ref.current;
    const ctx = cv?.getContext("2d");
    if (!cv || !ctx) return;
    let stars = makeStars(innerWidth > 700);
    let w = 0;
    let h = 0;
    const size = () => {
      const dpr = Math.min(2, devicePixelRatio || 1);
      w = innerWidth;
      h = innerHeight;
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      stars = makeStars(w > 700);
    };
    const draw = (t: number) => {
      ctx.clearRect(0, 0, w, h);
      const scroll = scrollY;
      for (const s of stars) {
        const ph = reduced ? 0.5 : ((((t - s.delay) % s.dur) + s.dur) % s.dur) / s.dur;
        const k = (1 - Math.cos(ph * 2 * Math.PI)) / 2;
        // Parallax: nearer stars drift further; wrap so the sky never runs out.
        const y = ((((s.y * h - (reduced ? 0 : scroll * 0.06 * s.depth)) % h) + h) % h);
        ctx.globalAlpha = 0.25 + 0.75 * k;
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x * w, y, s.r * (0.8 + 0.35 * k), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };
    size();
    let raf = 0;
    let last = 0;
    const loop = (now: number) => {
      if (now - last > 33) {
        last = now;
        draw(now / 1000);
      }
      raf = requestAnimationFrame(loop);
    };
    const onResize = () => {
      size();
      draw(performance.now() / 1000);
    };
    addEventListener("resize", onResize);
    if (reduced) draw(0);
    else raf = requestAnimationFrame(loop);
    return () => {
      removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, [reduced]);

  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
      <canvas ref={ref} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
